// Phase 5 binary touch detector (TOUCHING / NOT TOUCHING), built from the real Biotron recordings:
// CC90 rests on a plateau (slow drift, small ~7-unit steps); a touch is a fast slew (~30/sample over
// 0.3-0.5 s) to a far plateau that HOLDS, then a slew back. So the default feature is how far CC90 has
// departed from a rolling resting plateau, with hysteresis + min touch/release times.
export const FEATURES = ['ccDeparture', 'ccSlew', 'ccStd', 'pitchDeparture', 'velocity'];
export const FEATURE_FLOOR = { ccDeparture: 10, ccSlew: 12, ccStd: 4, pitchDeparture: 3, velocity: 8 };
export const DETECTOR_DEFAULT = { feature: 'ccDeparture', thrOn: 22, thrOff: 13, source: 'default', separation: null };
export const TOUCH5_DEFAULTS = {
  ccNumber: 90,
  frameMs: 50,
  featureWindowMs: 500,
  restWindowMs: 3000,
  minTouchMs: 120,
  minReleaseMs: 150,
  longContactMs: 500, // a contact older than this is a stroke/massage in progress: a wobbling hand must not split it
  longReleaseFrac: 0.35, // ...release only once it falls below 35% of its peak (or thrOff)...
  longReleaseMs: 150, // ...and stays there >= 150 ms (a dip that stays above it and climbs again is wobble, not a new touch)
  tapRise: 20, // quick tap: CC climbs >= 20 from its recent (2 s) low — one ~25-30/sample slide step, above two stacked 7-unit background steps
  tapBaseMs: 2000,
  tapExcursionMs: 800,
  riseFrac: 0.5, // a new touch must climb >= 50% of the threshold above the post-release trough
  releaseFrac: 0.5, // release once the value falls below half of the touch's peak (or below thrOff)
  holdFrac: 0.85, // "still holding" while within 85% of the peak; the release is timed from when it drops below
  restSlewMax: 10, // only settled frames (CC movement <= 10 over 0.5 s) feed the resting plateau, never a slide back
  onsetSlewMin: 18, // a real touch starts with a slide (~25-30/sample); a "touch" without one is baseline creep
  creepFlatMs: 1000, // ...and once it has sat flat for 1 s it becomes the new rest
  rebaselineAfterMs: 20000,
  symbolGapMs: 250, // a release shorter than this merges two touches into one symbol (measured gaps quantise to ~130 ms)
  tapMaxMs: 800, // ● TAP < 0.8 s · 0.8–1.0 s ambiguous (= the expected word)
  strokeMinMs: 1000, // ▬ STROKE 1.0–4.0 s
  strokeMaxMs: 4000, // 4.0–5.0 s ambiguous (= the expected word)
  massageMinMs: 5000, // ≋ MASSAGE ≥ 5.0 s (the live moan deepens exactly here)
  lenientStrokeMs: 600,
  lenientMassageMs: 3800,
  sensitivity: 3,
  minSeparation: 0.6,
  tapJumpMin: 14, // a quick tap starts with a real slide (one >= 14 step within 0.5 s); a hover approach creeps in slowly
  hoverOnFrac: 0.45, // untuned hover band = 45% / 30% of the touch threshold...
  hoverOffFrac: 0.3,
  hoverFloor: { ccDeparture: 15 }, // ...but above two stacked 7-unit background plateau steps
  hoverArmMs: 300, // in the band this long before SPORE notices a hover
  hoverMinMs: 1200, // TEASE symbol: >= 1.2 s hovering (from the approach) without touching
  hoverApproachMs: 400,
  hoverReleaseFrac: 0.5,
  hoverMaxMs: 6000, // a "hover" held longer becomes the (fast) rest
  fastRestMs: 1000,
  hoverAfterTouchMs: 700, // the slide back after a release passes through the band: not a hover
  hoverIsTouch: true, // hover is no longer a word: any departure above the (old) hover threshold is contact
};
// largest plausible touch threshold per feature: a tune above this was computed against a broken rest
export const FEATURE_MAX = { ccDeparture: 90, ccSlew: 200, ccStd: 60, pitchDeparture: 48, velocity: 90 };
export const sanitizeDetector = (d) => (d && d.feature && Number.isFinite(d.thrOn) && d.thrOn <= (FEATURE_MAX[d.feature] ?? Infinity) ? d : { ...DETECTOR_DEFAULT });
export const SENS_SCALE = { 1: 1.5, 2: 1.2, 3: 1, 4: 0.8, 5: 0.6 };
export const TOUCH_TEST_STEPS = [
  { prompt: 'HANDS OFF', on: false, ms: 4000 },
  { prompt: 'TOUCH & HOLD', on: true, ms: 3000 },
  { prompt: 'LET GO', on: false, ms: 3000 },
  { prompt: 'TOUCH & HOLD', on: true, ms: 3000 },
  { prompt: 'LET GO', on: false, ms: 3000 },
];

const median = (a) => {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const quantile = (a, q) => {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  return s[Math.min(s.length - 1, Math.max(0, Math.floor(q * (s.length - 1))))];
};

// cc: [{t, v}], notes: [{t, note, vel}] ascending; only samples at or before t are used.
export function featuresAt(cc, notes, t, rest, windowMs) {
  let lastCc = rest.cc;
  const w = [];
  for (let i = 0; i < cc.length && cc[i].t <= t; i += 1) {
    lastCc = cc[i].v;
    if (cc[i].t > t - windowMs) w.push(cc[i].v);
  }
  let slew = 0;
  let jump = 0;
  for (let i = 1; i < w.length; i += 1) {
    slew += Math.abs(w[i] - w[i - 1]);
    jump = Math.max(jump, Math.abs(w[i] - w[i - 1]));
  }
  const mean = w.length ? w.reduce((a, b) => a + b, 0) / w.length : lastCc;
  const std = w.length ? Math.sqrt(w.reduce((a, b) => a + (b - mean) ** 2, 0) / w.length) : 0;
  let lastNote = rest.note;
  const vels = [];
  for (let i = 0; i < notes.length && notes[i].t <= t; i += 1) {
    lastNote = notes[i].note;
    if (notes[i].t > t - windowMs) vels.push(notes[i].vel);
  }
  const vel = vels.length ? vels.reduce((a, b) => a + b, 0) / vels.length : rest.vel;
  return { ccDeparture: Math.abs(lastCc - rest.cc), ccSlew: slew, ccJump: jump, ccStd: std, pitchDeparture: Math.abs(lastNote - rest.note), velocity: Math.abs(vel - rest.vel), cc: lastCc, note: lastNote, vel };
}

// TAP < 0.8 s, STROKE 1.0–4.0 s, MASSAGE >= 5.0 s, in between = whatever is expected. lenient 1: >= 0.6 s is a stroke; lenient 2: anything is the expected one.
export function classifySymbol(durationMs, { expected = null, lenient = 0, cfg = TOUCH5_DEFAULTS } = {}) {
  const want = ['TAP', 'STROKE', 'MASSAGE'].includes(expected) ? expected : null;
  if (lenient >= 2 && want) return { symbol: want, ambiguous: false };
  if (lenient >= 1) return { symbol: durationMs >= cfg.lenientMassageMs ? 'MASSAGE' : durationMs >= cfg.lenientStrokeMs ? 'STROKE' : 'TAP', ambiguous: false };
  if (durationMs < cfg.tapMaxMs) return { symbol: 'TAP', ambiguous: false };
  if (durationMs >= cfg.massageMinMs) return { symbol: 'MASSAGE', ambiguous: false };
  if (durationMs >= cfg.strokeMinMs && durationMs <= cfg.strokeMaxMs) return { symbol: 'STROKE', ambiguous: false };
  if (durationMs < cfg.strokeMinMs) return { symbol: want === 'TAP' || want === 'STROKE' ? want : durationMs >= (cfg.tapMaxMs + cfg.strokeMinMs) / 2 ? 'STROKE' : 'TAP', ambiguous: true };
  return { symbol: want === 'STROKE' || want === 'MASSAGE' ? want : durationMs >= (cfg.strokeMaxMs + cfg.massageMinMs) / 2 ? 'MASSAGE' : 'STROKE', ambiguous: true };
}

// Labelled touch test -> best feature + hysteresis thresholds. windows: [{on, start, end}] (ms, same clock as samples).
export function autoTune({ cc, notes, windows }, { cfg = TOUCH5_DEFAULTS, onSettleMs = 600, offSettleMs = 900 } = {}) {
  const offs = windows.filter((w) => !w.on && !w.hover);
  if (!offs.length || !windows.some((w) => w.on) || cc.length < 10) return { feature: null, separation: 0, quality: 'poor', applied: false, reason: 'not enough data', perFeature: {}, hoverMode: 'degraded', hoverSeparation: 0, touchHoverSeparation: 0 };
  const first = offs[0];
  // the Biotron only sends on change: a silent HANDS OFF window means "still at the last value", not 0
  const held = (arr, k) => {
    const out = [];
    let i = -1;
    for (let t = first.start + 500; t <= first.end; t += cfg.frameMs) {
      while (i + 1 < arr.length && arr[i + 1].t <= t) i += 1;
      if (i >= 0) out.push(arr[i][k]);
    }
    return out;
  };
  const restCc = held(cc, 'v');
  if (!restCc.length) return { feature: null, separation: 0, quality: 'poor', applied: false, reason: 'no resting signal', perFeature: {}, hoverMode: 'degraded', hoverSeparation: 0, touchHoverSeparation: 0 };
  const rn = held(notes, 'note');
  const rv = held(notes, 'vel');
  const rest = { cc: median(restCc), note: rn.length ? median(rn) : 60, vel: rv.length ? median(rv) : 52 };
  const vals = Object.fromEntries(FEATURES.map((f) => [f, { on: [], off: [] }]));
  windows.filter((w) => !w.hover).forEach((w) => {
    for (let t = w.start + (w.on ? onSettleMs : offSettleMs); t <= w.end; t += cfg.frameMs) {
      const fs = featuresAt(cc, notes, t, rest, cfg.featureWindowMs);
      FEATURES.forEach((f) => vals[f][w.on ? 'on' : 'off'].push(fs[f]));
    }
  });
  const perFeature = {};
  FEATURES.forEach((f) => {
    const { on, off } = vals[f];
    if (!on.length || !off.length || median(on) <= median(off)) {
      perFeature[f] = { separation: 0, thrOn: null, thrOff: null };
      return;
    }
    let best = { ba: 0, thr: 0 };
    [...new Set([...on, ...off])].forEach((thr) => {
      const ba = (on.filter((x) => x >= thr).length / on.length + off.filter((x) => x < thr).length / off.length) / 2;
      if (ba > best.ba) best = { ba, thr };
    });
    const offHi = quantile(off, 0.95);
    const onLo = quantile(on, 0.1);
    const thrOn = Math.max(FEATURE_FLOOR[f], offHi < onLo ? (offHi + onLo) / 2 : best.thr);
    const thrOff = Math.min(thrOn * 0.85, Math.max(quantile(off, 0.9), thrOn * 0.6));
    perFeature[f] = { separation: Math.round((best.ba - 0.5) * 2 * 100) / 100, thrOn: Math.round(thrOn * 10) / 10, thrOff: Math.round(thrOff * 10) / 10, onMedian: median(on), offMedian: median(off) };
  });
  const feature = FEATURES.reduce((a, b) => (perFeature[b].separation > perFeature[a].separation ? b : a), FEATURES[0]);
  const p = perFeature[feature];
  const quality = p.separation >= 0.8 ? 'good' : p.separation >= cfg.minSeparation ? 'fair' : 'poor';
  const sane = p.thrOn !== null && p.thrOn <= (FEATURE_MAX[feature] ?? Infinity);
  const out = { feature, thrOn: p.thrOn, thrOff: p.thrOff, separation: p.separation, quality, applied: quality !== 'poor' && sane, rest, perFeature, ...(sane ? {} : { reason: 'threshold out of range' }) };
  return { ...out, ...tuneHover({ cc, notes, windows, rest, feature, thrOn: p.thrOn, thrOff: p.thrOff, on: vals[feature].on, off: vals[feature].off, cfg }) };
}

const balanced = (hi, lo, thr) => (hi.filter((x) => x >= thr).length / hi.length + lo.filter((x) => x < thr).length / lo.length) / 2;
// separation (0..1) of `hi` above `lo` at the best threshold
const separate = (hi, lo) => {
  if (!hi.length || !lo.length || median(hi) <= median(lo)) return 0;
  const ba = [...new Set([...hi, ...lo])].reduce((b, thr) => Math.max(b, balanced(hi, lo, thr)), 0.5);
  return Math.round((ba - 0.5) * 2 * 100) / 100;
};

// Hover window -> REST / HOVER / TOUCH bands. Poor separation (either side) = degraded mode.
function tuneHover({ cc, notes, windows, rest, feature, thrOn, thrOff, on, off, cfg }) {
  const hw = windows.filter((w) => w.hover);
  if (!hw.length || !thrOn) return { hoverMode: 'untuned', hoverSeparation: null, touchHoverSeparation: null };
  const hv = [];
  const offH = [];
  hw.forEach((w) => {
    // judge the hover against the level just before it (the LET GO step), not the first window: the rest may have stepped since
    const prev = windows[windows.indexOf(w) - 1];
    const near = (x) => prev && x.t >= prev.end - 1000 && x.t <= prev.end;
    const local = prev ? { ...rest, cc: median(cc.filter(near).map((x) => x.v)) || rest.cc, note: median(notes.filter(near).map((x) => x.note)) || rest.note } : rest;
    for (let t = w.start + 800; t <= w.end; t += cfg.frameMs) hv.push(featuresAt(cc, notes, t, local, cfg.featureWindowMs)[feature]);
    if (prev) for (let t = prev.end - 1000; t <= prev.end; t += cfg.frameMs) offH.push(featuresAt(cc, notes, t, local, cfg.featureWindowMs)[feature]);
  });
  const offRef = offH.length ? offH : off; // hands-off just before the hover, on the same local rest
  const top = [...hv].sort((a, b) => a - b).slice(hv.length >> 1); // people react late: judge the steadier half of the window
  const hoverSeparation = separate(top, offRef);
  const touchHoverSeparation = separate(on, top);
  const hoverMedian = median(top);
  if (Math.min(hoverSeparation, touchHoverSeparation) < cfg.minSeparation || hoverMedian < FEATURE_FLOOR[feature]) return { hoverMode: 'degraded', hoverSeparation, touchHoverSeparation, hoverMedian };
  const hoverOn = Math.max(FEATURE_FLOOR[feature] * 0.5, quantile(offRef, 0.95) + 1, (quantile(offRef, 0.95) + hoverMedian) / 2);
  const hoverOff = Math.min(hoverOn * 0.85, Math.max(quantile(offRef, 0.9), hoverOn * 0.65));
  const hvHi = quantile(top, 0.9);
  const onLo = quantile(on, 0.1);
  const thrOn2 = hvHi < onLo ? Math.max(thrOn, (hvHi + onLo) / 2) : thrOn; // the touch band sits above the hover band
  const r1 = (x) => Math.round(x * 10) / 10;
  return {
    hoverMode: 'tuned', hoverOn: r1(hoverOn), hoverOff: r1(hoverOff), tapMin: r1(Math.min(thrOn2, Math.max(hoverOn, hoverMedian * 0.85))), // well under a typical hover; the slide (jump) rule rejects slow approaches
    thrOn: r1(thrOn2), thrOff: r1(thrOff * (thrOn2 / thrOn)), hoverSeparation, touchHoverSeparation, hoverMedian,
  };
}

export function createBinaryTouch(config = {}, { detector = DETECTOR_DEFAULT, startT = 0 } = {}) {
  let cfg = { ...TOUCH5_DEFAULTS, ...config };
  let det = { ...DETECTOR_DEFAULT, ...detector };
  const listeners = new Set();
  let cc = [];
  let notes = [];
  let restBuf = [];
  let rest = null;
  let touching = false;
  let touchStart = null;
  let candSince = null;
  let relSince = null;
  let lastFrame = startT;
  let lastT = startT;
  let restSince = startT;
  let rebaselines = 0;
  let feats = {};
  let value = 0;
  let sym = null;
  let lastSymbol = null;
  let trough = 0;
  let peak = 0;
  let fenceT = -Infinity;
  let onsetSlew = 0;
  let creep = false;
  let flatSince = null;
  let dirSign = 1; // which way CC90 moves when touched (learned from each sustained touch)
  let svHist = [];
  let lastHighT = startT;
  let dipMin = Infinity;
  let ex = null;
  let hover = null;
  let fastBuf = [];
  let lastLowT = -Infinity;
  let postLow = null;
  const relearnFast = () => {
    fastBuf = [];
    postLow = Infinity;
  };
  let lastTouchEnd = -Infinity;
  const emit = (type, t, extra = {}) => listeners.forEach((fn) => fn({ type, t, ...extra }));
  const thr = () => {
    const k = SENS_SCALE[cfg.sensitivity] || 1;
    // a measured light-contact band (tuned hover) is contact now; an untuned band would sit inside background drift
    if (!cfg.hoverIsTouch || det.hoverMode !== 'tuned' || !Number.isFinite(det.hoverOn)) return { on: det.thrOn * k, off: det.thrOff * k };
    const h = hoverThr();
    return { on: Math.min(det.thrOn * k, h.on), off: Math.min(det.thrOff * k, h.off) };
  };
  const hoverThr = () => {
    const k = SENS_SCALE[cfg.sensitivity] || 1;
    return { on: Math.max(det.hoverOn ?? Math.max(det.thrOn * cfg.hoverOnFrac, cfg.hoverFloor[det.feature] ?? 0), 0) * k, off: (det.hoverOff ?? Math.max(det.thrOn * cfg.hoverOffFrac, (cfg.hoverFloor[det.feature] ?? 0) * 0.7)) * k, tapMin: (det.tapMin ?? 0) * k };
  };
  function endHover(t, reason) {
    if (!hover) return;
    if (reason === 'withdrawn') relearnFast();
    if (hover.armed) emit('HOVER_END', t, { durationMs: Math.round(t - hover.start), reason, symbol: hover.emitted });
    hover = null;
  }
  // REST / HOVER: a hand held near moves the signal into a smaller band below TOUCH
  function hoverTick(t) {
    if (cfg.hoverIsTouch) return;
    const hb = hoverThr();
    // a hover is judged against a FAST rest (settled frames of the last 1 s): plateau steps (single 7-unit jumps) are
    // absorbed in ~0.5 s so two of them never stack into a "hover"; a hand near freezes it
    let back = null; // CC 300 ms ago: a frame that is still climbing (a slow hover approach) is not settled
    for (let k = cc.length - 1; k >= 0; k -= 1) if (cc[k].t <= t - 300) { back = cc[k].v; break; }
    const climbing = back !== null && dirSign * (feats.cc - back) > 2;
    const settled = !touching && !hover && candSince === null && !ex && feats.ccSlew <= cfg.restSlewMax && !climbing;
    if (settled) fastBuf.push({ t, cc: feats.cc, v: value });
    while (fastBuf.length > 4 && fastBuf[0].t < t - cfg.fastRestMs) fastBuf.shift();
    // right after a hover/touch ends the drop is still a slide (no settled frames yet): the lowest level since then is the rest
    if (postLow !== null) postLow = fastBuf.length >= 3 ? null : Math.min(postLow, dirSign * feats.cc);
    const fast = postLow !== null ? dirSign * postLow : fastBuf.length ? median(fastBuf.map((x) => x.cc)) : rest.cc;
    const hv = det.feature === 'ccDeparture' ? dirSign * (feats.cc - fast) : value - (fastBuf.length ? median(fastBuf.map((x) => x.v)) : 0);
    if (touching || candSince !== null || ex) endHover(t, 'touch');
    else if (!hover) {
      if (hv < hb.off) lastLowT = t;
      // timed from where the approach began (a slow hand takes ~0.3 s to reach the band), at most hoverApproachMs back
      if (hv >= hb.on && !sym && t - lastTouchEnd >= cfg.hoverAfterTouchMs) hover = { start: Math.max(lastLowT, t - cfg.hoverApproachMs, lastTouchEnd, fenceT), armed: false, emitted: false, peak: hv };
    } else if (hv < Math.max(hb.off, hover.peak * cfg.hoverReleaseFrac)) endHover(t, 'withdrawn'); // withdrawal = a clear drop from the hover peak
    else {
      hover.peak = Math.max(hover.peak, hv);
      if (!hover.armed && t - hover.start >= cfg.hoverArmMs) {
        hover.armed = true;
        emit('HOVER_START', hover.start);
      }
      if (!hover.emitted && t - hover.start >= cfg.hoverMinMs) {
        hover.emitted = true;
        emit('HOVER_SYMBOL', t, { durationMs: Math.round(t - hover.start), symbol: 'TEASE', stale: hover.start < fenceT });
      }
      if (t - hover.start >= cfg.hoverMaxMs) {
        fastBuf = [{ t, cc: feats.cc, v: value }]; // a hand held near this long becomes the (fast) rest
        endHover(t, 'absorbed');
      }
    }
  }

  function ingest(e) {
    if (e.type === 'cc' && e.cc === cfg.ccNumber) {
      cc.push({ t: e.t, v: e.value });
      if (!rest) rest = { cc: e.value, note: 60, vel: 52 };
    } else if (e.type === 'note_on' && e.velocity > 0) notes.push({ t: e.t, note: e.note, vel: e.velocity });
  }

  function pushRest(t) {
    restBuf.push({ t, cc: feats.cc, note: feats.note, vel: feats.vel });
    while (restBuf.length > 40 && restBuf[0].t < t - cfg.restWindowMs) restBuf.shift(); // keep >= 2 s of resting frames even after long touches
    rest = { cc: median(restBuf.map((x) => x.cc)), note: median(restBuf.map((x) => x.note)), vel: median(restBuf.map((x) => x.vel)) };
  }

  function startTouch(t0) {
    touching = true;
    touchStart = t0;
    candSince = null;
    relSince = null;
    peak = value;
    lastHighT = lastT;
    dipMin = Infinity;
    flatSince = null;
    // the Biotron only sends on change: one jump then silence is a real touch, so the slide is measured from the value held before
    let held = rest ? rest.cc : feats.cc;
    for (let k = cc.length - 1; k >= 0; k -= 1) if (cc[k].t <= t0 - cfg.featureWindowMs) { held = cc[k].v; break; }
    creep = Math.max(onsetSlew, Math.abs(feats.cc - held)) < cfg.onsetSlewMin; // no real slide at onset: a baseline creep, not a hand
    onsetSlew = 0;
    if (rest && Math.abs(feats.cc - rest.cc) >= thr().on) dirSign = Math.sign(feats.cc - rest.cc);
    if (sym && sym.end !== null && t0 - sym.end < cfg.symbolGapMs && sym.start >= fenceT) {
      sym.end = null;
      emit('TOUCH_RESUME', t0);
    } else {
      sym = { start: t0, end: null };
      emit('TOUCH_START', t0, { creep });
    }
  }

  function endTouch(tEnd) {
    touching = false;
    relSince = null;
    dipMin = Infinity;
    lastTouchEnd = lastT;
    relearnFast();
    svHist = rest ? [{ t: lastT, v: dirSign * (feats.cc - rest.cc) }] : []; // the next tap climbs from where the slide back is now
    sym.end = tEnd;
    emit('TOUCH_END', tEnd, { durationMs: Math.round(tEnd - sym.start) });
  }

  function rebaseline(t, reason) {
    restBuf = [];
    const recent = cc.filter((x) => x.t > t - 1000).map((x) => x.v);
    if (recent.length) rest = { ...(rest || { note: 60, vel: 52 }), cc: median(recent) };
    if (touching) emit('TOUCH_END', t, { durationMs: Math.round(t - sym.start), reason });
    touching = false;
    candSince = null;
    relSince = null;
    ex = null;
    hover = null;
    fastBuf = [];
    svHist = [];
    trough = 0;
    sym = null;
    rebaselines += 1;
    restSince = t;
    emit('REBASELINE', t, { reason, rest: { ...rest } });
  }

  // a stalled main thread (audio decode, re-render) must not skip a one-sample tap: evaluate each CC sample we'd miss
  let evalCcT = -Infinity;
  function tick(t) {
    if (t - lastFrame >= cfg.frameMs) {
      const pending = cc.filter((x) => x.t > evalCcT && x.t > lastFrame && x.t < t);
      pending.slice(0, -1).forEach((x) => {
        lastT = x.t + 1;
        frame(x.t + 1);
      });
      if (cc.length) evalCcT = cc[cc.length - 1].t;
    }
    lastT = t;
    if (t - lastFrame < cfg.frameMs) return getState();
    return frame(t);
  }

  function frame(t) {
    lastFrame = t;
    while (cc.length && cc[0].t < t - 10000) cc.shift();
    while (notes.length && notes[0].t < t - 10000) notes.shift();
    if (!rest) return getState();
    feats = featuresAt(cc, notes, t, rest, cfg.featureWindowMs);
    value = feats[det.feature] ?? 0;
    const { on, off } = thr();
    const rise = on * cfg.riseFrac;
    if (!touching) {
      trough = Math.min(trough, value);
      const armed = value - trough >= rise; // must climb away from the post-release trough, not just still be sliding back
      // signed departure in the device's touch direction, so a cc sitting slightly on the far side of rest doesn't fold the rise
      const sv = dirSign * (feats.cc - rest.cc);
      if (!ex) {
        svHist.push({ t, v: sv });
        while (svHist.length && svHist[0].t < t - cfg.tapBaseMs) svHist.shift();
      }
      const svBase = svHist.reduce((a, x) => Math.min(a, x.v), sv);
      onsetSlew = candSince !== null || ex ? Math.max(onsetSlew, feats.ccSlew) : feats.ccSlew;
      if (value >= on && armed) {
        candSince = candSince ?? t;
        if (t - candSince >= cfg.minTouchMs) {
          startTouch(ex ? ex.start : candSince);
          ex = null;
        }
      } else candSince = null;
      if (!touching) {
        if (!ex && sv - svBase >= cfg.tapRise && feats.ccJump >= cfg.tapJumpMin) ex = { start: t, base: svBase, peak: sv, lastHigh: t, peakV: value };
        else if (ex) {
          // quick tap: a clear climb (>= tapRise from the recent low) that heads back half-way before the sustained rule confirms it
          ex.peak = Math.max(ex.peak, sv);
          ex.peakV = Math.max(ex.peakV, value);
          const h = ex.peak - ex.base;
          if (sv - ex.base >= h * cfg.holdFrac) ex.lastHigh = t;
          if (sv - ex.base <= h * cfg.releaseFrac && h < hoverThr().tapMin) {
            ex = null; // never reached the learned tap magnitude: a hover approach + withdrawal, not a tap
            trough = value;
          } else if (sv - ex.base <= h * cfg.releaseFrac) {
            startTouch(ex.start);
            endTouch(Math.max(ex.lastHigh, ex.start + cfg.frameMs));
            ex = null;
            candSince = null;
            trough = value;
          } else if (t - ex.start > cfg.tapExcursionMs) {
            ex = null;
            trough = value; // plateau shift, not a tap: re-arm only on a fresh climb
            svHist = [{ t, v: sv }];
          }
        }
      }
      if (!touching) hoverTick(t);
      else endHover(t, 'touch');
      if (!touching && !hover && candSince === null && (!ex || value < on) && feats.ccSlew <= cfg.restSlewMax) pushRest(t); // settled frames only: slow drift yes, slides no, a hand held near no
    } else {
      peak = Math.max(peak, value);
      if (value >= peak * cfg.holdFrac) lastHighT = t;
      // release on a clear move back toward rest (half-way from the touch's peak), not only on full return;
      // a dip that climbs again is a release + new touch. Release time = when the slide back began.
      const long = t - touchStart >= cfg.longContactMs;
      const relLvl = long ? Math.max(off, peak * cfg.longReleaseFrac) : Math.max(off, peak * cfg.releaseFrac);
      const relMs = long ? cfg.longReleaseMs : cfg.minReleaseMs;
      if (relSince !== null && value - dipMin >= rise && (!long || dipMin < relLvl)) {
        const dip = dipMin;
        endTouch(lastHighT);
        trough = dip;
        svHist = [{ t, v: dip }]; // the new touch climbs from the dip, not from where it already is
      } else if (value < relLvl) {
        relSince = relSince ?? t;
        dipMin = Math.min(dipMin, value);
        if (t - relSince >= relMs) {
          endTouch(lastHighT);
          trough = value;
        }
      } else {
        relSince = null;
        dipMin = Infinity;
      }
      if (touching && t - touchStart >= cfg.rebaselineAfterMs) rebaseline(t, 'touch-over-20s');
      else if (touching) {
        flatSince = feats.ccSlew <= cfg.restSlewMax ? flatSince ?? t : null;
        if (creep && flatSince !== null && t - flatSince >= cfg.creepFlatMs) rebaseline(t, 'creep'); // the rest moved; nobody is touching
      }
    }
    if (sym && sym.end !== null && !touching && t - sym.end >= cfg.symbolGapMs) {
      const durationMs = Math.round(sym.end - sym.start);
      const stale = sym.start < fenceT;
      lastSymbol = { ...classifySymbol(durationMs, { cfg }), durationMs, t, stale };
      sym = null;
      emit('SYMBOL', t, { durationMs, symbol: lastSymbol.symbol, ambiguous: lastSymbol.ambiguous, stale });
    }
    return getState();
  }

  function getState() {
    const { on, off } = thr();
    const hb = hoverThr();
    const hovering = !touching && !!(hover && hover.armed);
    return {
      touching, touchActive: touching, touchMs: touching && sym ? lastT - sym.start : 0, lastSymbol, value, features: { ...feats },
      hovering, hoverMs: hovering ? lastT - hover.start : 0, state3: touching ? 'TOUCH' : hovering ? 'HOVER' : 'REST',
      detector: { ...det, thrOnScaled: on, thrOffScaled: off, hoverOnScaled: hb.on, hoverOffScaled: hb.off, hoverMode: det.hoverMode || 'untuned' }, rest: rest ? { ...rest } : null, rebaselines, restAgeS: (lastT - restSince) / 1000,
      sensitivity: cfg.sensitivity,
    };
  }

  return {
    ingest,
    tick,
    getState,
    relearn(t = lastT) {
      rest = null;
      rebaseline(t, 'relearn');
    },
    setDetector(d) {
      det = { ...DETECTOR_DEFAULT, ...d };
    },
    getDetector: () => ({ ...det }),
    getBaseline: () => ({ rest: rest ? { ...rest } : null, rebaselines, ageS: (lastT - restSince) / 1000 }),
    // TRY starts: touches that began before now are never merged with, or counted as, new input
    fence(t = lastT) {
      fenceT = t;
    },
    setBaseline() {},
    getConfig: () => ({ ...cfg }),
    setConfig(partial) {
      cfg = { ...cfg, ...partial };
    },
    on(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
